import {
    IClientCategories,
    ICompanyGroups,
    ICompanyGroupV2,
    IExcelUser,
    IInternationalCompany,
    ITools,
    IUser,
    IUserGroup,
    TOptionalUser,
    TUser,
} from 'pages/UserManagement/types';
import { IRetailer } from 'shared/types';
import { cdmApi } from '.';

const PREFIX = '/users';

const usersApi = cdmApi.injectEndpoints({
    endpoints: (builder) => ({
        getUsers: builder.query<{ users: IUser[] }, void>({
            query: () => ({ url: `${PREFIX}/`, method: 'get' }),
            providesTags: () => [{ type: 'Users', id: 'LIST' }],
        }),
        getUsersV2: builder.query<{ users: TUser[] }, void>({
            query: () => ({ url: `${PREFIX}/v2`, method: 'get' }),
            providesTags: () => [{ type: 'Users', id: 'LIST' }],
        }),
        getInternationalUsers: builder.query<{ users: TUser[] }, void>({
            query: () => ({ url: `${PREFIX}/international-users`, method: 'get' }),
            providesTags: () => [{ type: 'InternationalUsers', id: 'LIST' }],
        }),
        addUser: builder.mutation<{ code: number; message: string; users: IUser[] }, TOptionalUser>({
            query: (data) => ({ url: `${PREFIX}/add`, method: 'post', data }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                {
                    type: 'InternationalUsers',
                    id: 'LIST',
                },
            ],
        }),

        updateUser: builder.mutation<
            { code: number; message: string; users: any },
            { id: string; data: TOptionalUser }
        >({
            query: ({ id, data }) => ({ url: `${PREFIX}/update/${id}`, method: 'put', data }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                {
                    type: 'InternationalUsers',
                    id: 'LIST',
                },
            ],
        }),

        updateInternationalUser: builder.mutation<{ code: number; message: string; users: IUser[] }, IUser>({
            query: (data) => ({ url: `${PREFIX}/update?isInternational=true`, method: 'put', data }),
            invalidatesTags: [
                { type: 'Users', id: 'LIST' },
                {
                    type: 'InternationalUsers',
                    id: 'LIST',
                },
            ],
        }),
        resetUserPassword: builder.mutation<{ code: number }, number[]>({
            query: (data) => ({ url: `${PREFIX}/reset-password`, method: 'post', data }),
        }),
        exportUsers: builder.query<any, { isTemplate?: boolean; isInternational?: boolean }>({
            query: (params) => ({
                url: `${PREFIX}/excel/export`,
                method: 'get',
                params,
                responseType: 'arraybuffer',
            }),
        }),
        uploadUserExcel: builder.mutation<{ code: number; arrayErrors: IExcelUser[]; users: IUser[] }, FormData>({
            query: (data) => ({ url: `${PREFIX}/excel/upload`, method: 'post', data }),
            invalidatesTags: ['Users'],
        }),
        exportUserUploadErrorExcel: builder.mutation<any, IExcelUser[]>({
            query: (data) => ({
                url: `${PREFIX}/excel/errors`,
                method: 'post',
                responseType: 'arraybuffer',
                data,
            }),
        }),
        getUsersGroup: builder.query<{ code: number; message: string; groups: IUserGroup[] }, void>({
            query: () => ({ url: `${PREFIX}/group/`, method: 'get' }),
        }),
        getCompanyGroup: builder.query<{ code: number; message: string; companyGroup: ICompanyGroups[] }, void>({
            query: () => ({ url: `/company/`, method: 'get' }),
            providesTags: ['CompanyGroup'],
        }),
        GetInternationalCompany: builder.query<
            { code: number; message: string; internationalCompanies: IInternationalCompany[] },
            void
        >({
            query: () => ({ url: `/company/international-company/`, method: 'get' }),
        }),
        deleteCompanyGroup: builder.mutation<{ code: number; companyGroupData: ICompanyGroups }, { id: number[] }>({
            query: (data) => ({ url: `/company/delete`, method: 'delete', data }),
            invalidatesTags: ['Users'],
            onQueryStarted(companyGroup, { dispatch, queryFulfilled }) {
                const res = dispatch(
                    usersApi.util.updateQueryData('getCompanyGroup', undefined, (draft) => {
                        const draftRef = draft;
                        const newCompanyGroup = draftRef.companyGroup.filter((k) => k.id !== companyGroup.id[0]);
                        draftRef.companyGroup = newCompanyGroup;
                    }),
                );

                queryFulfilled.catch(() => {
                    res.undo();
                });
            },
        }),
        updateCompanyGroup: builder.mutation<
            { code: number; companyGroupData: ICompanyGroups },
            ICompanyGroups | ICompanyGroupV2
        >({
            query: (data) => ({ url: `/company/update`, method: 'put', data }),
            invalidatesTags: ['Users', 'CompanyGroup'],
        }),

        getCompanyGroupsByCompanyId: builder.query<any, number>({
            query: (companyId) => ({
                url: `/company/company-groups/${companyId}`,
                method: 'get',
            }),
        }),

        addCompanyGroup: builder.mutation<
            { code: number; companyGroupData: ICompanyGroups },
            ICompanyGroups | ICompanyGroupV2
        >({
            query: (data) => ({ url: `/company/add`, method: 'post', data }),
            invalidatesTags: ['Users', 'CompanyGroup'],
        }),
        getTools: builder.query<{ code: number; tools: ITools[] }, void>({
            query: () => ({ url: `/users/tools`, method: 'get' }),
        }),
        getCategoriesClient: builder.query<{ code: number; categories_clients: IClientCategories[] }, void>({
            query: () => ({ url: `/company/categories`, method: 'get' }),
        }),
        getToolsDetails: builder.query<
            { code: number; tools: ITools[]; retailers: IRetailer[]; client_categories: IClientCategories[] },
            void
        >({
            query: () => ({ url: `/company/details`, method: 'get' }),
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetCategoriesClientQuery,
    useGetUsersQuery,
    useGetUsersV2Query,
    useGetInternationalUsersQuery,
    useGetInternationalCompanyQuery,
    useGetToolsQuery,
    useGetToolsDetailsQuery,
    useGetCompanyGroupQuery,
    useUpdateCompanyGroupMutation,
    useDeleteCompanyGroupMutation,
    useAddCompanyGroupMutation,
    useUpdateUserMutation,
    useUpdateInternationalUserMutation,
    useAddUserMutation,
    useResetUserPasswordMutation,
    useUploadUserExcelMutation,
    useExportUserUploadErrorExcelMutation,
    useLazyExportUsersQuery,
    useGetUsersGroupQuery,
    useLazyGetCompanyGroupsByCompanyIdQuery,
} = usersApi;
